import { injectable } from 'inversify';

import {
  CreateEventTypeRequest,
  CreateWorkflowTransition,
  DeadlineSettings,
  EventTypeWorkflowNode,
  EventTypeWorkflowNodeAction,
  UpdateEventTypeRequest,
  WorkflowNodeActionAttribute,
  WorkflowNodeActionRole,
} from '@app/gen/settings';

import {
  CreateEventTypeDto,
  NodeData,
  DeadlineDto,
  NodeActionDto,
  NodeActionAttributeDto,
  FlowEdgeDto,
  BranchTaskDto,
  UserTaskDto,
  StartTaskDto,
} from '../../dto';
import {
  START_EVENT_NODE_ID,
  FINISH_ACTION_NAME_MAP,
  TASK_ACTION_NAME_MAP,
  ROLE_EXECUTOR_MAP,
} from '../../constants';
import { FlowNodeType } from '../../types';

type EventTypeRequestBody = CreateEventTypeRequest & UpdateEventTypeRequest;

type ProcessableNode = Exclude<NodeData, { type: 'ParallelGroup' }>;
type NodeWithDeadline = UserTaskDto | BranchTaskDto;

type ProcessNodeArgs<K extends FlowNodeType> = {
  nodeId: string;
  node: Extract<ProcessableNode, { type: K }>;
  joinAny: boolean;
};

type NodeProcessorResult = Omit<EventTypeWorkflowNode, 'key' | 'join_any'>;

type NodeProcessor<K extends FlowNodeType> = (
  args: ProcessNodeArgs<K>,
) => NodeProcessorResult;

@injectable()
export class EventTypeFormMapper {
  private processDeadline(
    deadlineData?: DeadlineDto[],
    deadlineMode?: DeadlineSettings['mode'],
  ): DeadlineSettings | undefined {
    if (!deadlineData?.length) return undefined;

    return {
      mode: deadlineMode,
      settings: deadlineData.map((deadline) => ({
        reference: deadline?.reference,
        time_unit: deadline?.unit,
        term: deadline?.term,
        round_days: deadline?.round === 'true',
        outcome: deadline?.outcome,
        attribute:
          deadline?.reference === 'attribute' && deadline?.attribute
            ? { id: deadline?.attribute, name: deadline?.attributeName ?? '' }
            : undefined,
      })),
    };
  }

  private getDefaultActionName(
    actionType: NodeActionDto['type'],
    nodeType: 'FinishEvent' | 'UserTask' | 'BranchTask',
  ): string | undefined {
    if (!actionType) return undefined;

    const nameMap =
      nodeType === 'FinishEvent'
        ? FINISH_ACTION_NAME_MAP
        : TASK_ACTION_NAME_MAP;

    return nameMap[actionType];
  }

  private buildSystemAction(
    name?: string,
    node?: NodeWithDeadline | StartTaskDto,
  ): EventTypeWorkflowNodeAction {
    return {
      type: 'system',
      name: name ?? '',
      is_primary: true,
      is_employee: !!node?.executor?.includes('employee'),
      roles: this.processActionRoles(node),
    };
  }

  private processAttributes(
    attributes?: NodeActionAttributeDto[],
  ): WorkflowNodeActionAttribute[] | undefined {
    if (!attributes) return undefined;

    return attributes.map((attribute) => {
      const mappedAttribute: WorkflowNodeActionAttribute = {
        id: attribute.id,
        name: attribute.name,
        type: attribute.type,
        required: attribute.required,
        is_multiple: attribute.isMultiple,
      };

      if (attribute.choices) {
        mappedAttribute.options = { choices: attribute.choices };
      }

      if (attribute.catalogName) {
        mappedAttribute.options = {
          ...mappedAttribute.options,
          catalog_name: attribute.catalogName,
        };
      }

      return mappedAttribute;
    });
  }

  private processActionRoles(
    node?: NodeWithDeadline | StartTaskDto,
  ): WorkflowNodeActionRole[] | undefined {
    return node?.executor.flatMap((type) => ROLE_EXECUTOR_MAP[type](node));
  }

  private sortByPrimaryFirst<T extends { isPrimary?: boolean }>(
    actions: T[],
  ): T[] {
    const primaryIndex = actions.findIndex((a) => a.isPrimary);

    if (primaryIndex <= 0) return actions;

    const primary = actions[primaryIndex];
    const rest = actions.filter((_, index) => index !== primaryIndex);
    return [primary, ...rest];
  }

  private buildAction(
    action: NodeActionDto,
    nodeType: 'UserTask' | 'BranchTask',
    roleFields: Pick<EventTypeWorkflowNodeAction, 'is_employee' | 'roles'>,
  ): EventTypeWorkflowNodeAction {
    return {
      name: action.name ?? this.getDefaultActionName(action.type, nodeType),
      type: action.type ?? 'accept',
      document_type_name: action.documentName,
      signature_type: action.signType,
      is_primary: action.isPrimary,
      attributes: this.processAttributes(action.attributes),
      ...roleFields,
    };
  }

  private processActions(
    node: UserTaskDto | BranchTaskDto,
  ): EventTypeWorkflowNodeAction[] {
    const actions = this.sortByPrimaryFirst(node.actions);

    const isEmployee = node.executor?.includes('employee');

    return actions.map((action) =>
      this.buildAction(action, node.type, {
        is_employee: isEmployee,
        roles: this.processActionRoles(node),
      }),
    );
  }

  private processTaskWithDeadline = (
    node: NodeWithDeadline,
  ): Omit<EventTypeWorkflowNode, 'key' | 'join_any'> => ({
    type: 'middle',
    name: node.stageName,
    description: node.stageDescription,
    employee_cancelable: node.employeeCancelable,
    company_cancelable: node.companyCancelable,
    notify_integration: node.notifyIntegration,
    deadline_settings: this.processDeadline(node.deadline, node.deadlineMode),
    actions: this.processActions(node),
  });

  private nodeProcessors: { [K in FlowNodeType]: NodeProcessor<K> } = {
    StartEvent: ({ node }) => ({
      type: 'begin',
      name: node.stageName,
      notify_integration: node.notifyIntegration,
      employee_cancelable: false,
      company_cancelable: false,
      actions: [this.buildSystemAction('Старт', node)],
    }),

    FinishEvent: ({ node }) => {
      const finishActionName = this.getDefaultActionName(
        node.actions[0]?.type,
        'FinishEvent',
      );

      return {
        type: 'end',
        name: 'Финиш',
        notify_integration: node.notifyIntegration,
        employee_cancelable: false,
        company_cancelable: false,
        actions: [this.buildSystemAction(finishActionName)],
      };
    },

    SystemTask: ({ node }) => ({
      type: 'middle',
      name: node.stageName || 'Обработка',
      notify_integration: false,
      employee_cancelable: false,
      company_cancelable: false,
      actions: [this.buildSystemAction()],
    }),

    UserTask: ({ node }) => this.processTaskWithDeadline(node),
    BranchTask: ({ node }) => this.processTaskWithDeadline(node),
  };

  private getStartNode(nodes: CreateEventTypeDto['nodes']) {
    const startNode = nodes[START_EVENT_NODE_ID];

    return startNode?.type === 'StartEvent' ? startNode : undefined;
  }

  private findParallelGroup(
    nodes: CreateEventTypeDto['nodes'],
    nodeId: string,
  ) {
    const node = nodes[nodeId];

    if (node?.type !== 'UserTask' && node?.type !== 'BranchTask') return;

    if (!node.parallelGroupId) return;

    const group = nodes[node.parallelGroupId];

    return group?.type === 'ParallelGroup' ? group : undefined;
  }

  private getJoinAnyByTargetId(
    nodes: CreateEventTypeDto['nodes'],
    edges: CreateEventTypeDto['edges'] = [],
  ): Map<string, boolean> {
    const result = new Map<string, boolean>();

    for (const { sourceId, targetId } of edges) {
      const group = this.findParallelGroup(nodes, sourceId);

      if (group) result.set(targetId, group.joinAny === 'true');
    }

    return result;
  }

  private processNode<K extends FlowNodeType>({
    nodeId,
    node,
    joinAny,
  }: ProcessNodeArgs<K>): EventTypeWorkflowNode {
    return {
      key: nodeId,
      join_any: joinAny,
      ...this.nodeProcessors[node.type]({ nodeId, node, joinAny }),
    };
  }

  private processNodes(
    creationData: CreateEventTypeDto,
  ): EventTypeWorkflowNode[] {
    const joinAnyByTargetId = this.getJoinAnyByTargetId(
      creationData.nodes,
      creationData.edges,
    );

    return Object.entries(creationData.nodes).flatMap(([nodeId, node]) => {
      if (node.type === 'ParallelGroup') return [];

      return [
        this.processNode({
          nodeId,
          node,
          joinAny: joinAnyByTargetId.get(nodeId) ?? false,
        }),
      ];
    });
  }

  private resolveIds(nodes: CreateEventTypeDto['nodes'], id: string): string[] {
    if (nodes[id]?.type !== 'ParallelGroup') return [id];

    return Object.entries(nodes)
      .filter(
        (entry): entry is [string, UserTaskDto] =>
          entry[1].type === 'UserTask' && entry[1].parallelGroupId === id,
      )
      .map(([key]) => key);
  }

  private resolveTransitionActionType(
    node: NodeData | undefined,
    condition?: FlowEdgeDto['condition'],
  ): CreateWorkflowTransition['action_type'] {
    if (node?.type !== 'UserTask' && node?.type !== 'BranchTask')
      return 'system';

    if (node.type === 'BranchTask' && condition === 'secondary') {
      return (
        node.actions.find((a) => !a.isPrimary && a.type !== 'return')?.type ??
        'decline'
      );
    }

    return (
      (node.actions.find((action) => action.isPrimary) ?? node.actions[0])
        ?.type ?? 'accept'
    );
  }

  private processTransitions(
    creationData: CreateEventTypeDto,
  ): CreateWorkflowTransition[] {
    const { nodes, edges = [], returns = [] } = creationData;

    const buildTransitionsForEdge = (
      edge: FlowEdgeDto,
      getType: (from: string) => CreateWorkflowTransition['action_type'],
    ) =>
      this.resolveIds(nodes, edge.sourceId).flatMap((from) =>
        this.resolveIds(nodes, edge.targetId).map((to) => ({
          from_node_key: from,
          to_node_key: to,
          action_type: getType(from),
        })),
      );

    return [
      ...edges.flatMap((edge) =>
        buildTransitionsForEdge(edge, (from) =>
          this.resolveTransitionActionType(nodes[from], edge.condition),
        ),
      ),
      ...returns.flatMap((edge) =>
        buildTransitionsForEdge(edge, () => 'return' as const),
      ),
    ];
  }

  processFormToRequestBody(
    creationData: CreateEventTypeDto,
  ): EventTypeRequestBody {
    const startNode = this.getStartNode(creationData.nodes);

    return {
      event_type: {
        company_id: creationData.companyId,
        name: creationData.eventType,
        event_type_group: creationData?.groupTypes,
        custom_type: startNode?.noEmployee
          ? undefined
          : creationData.customType,
        batch_enabled: !!startNode?.batchEnabled,
        create_for_others: !!startNode?.createForOthers,
        no_employee: !!startNode?.noEmployee,
      },
      workflow: {
        nodes: this.processNodes(creationData),
        transitions: this.processTransitions(creationData),
      },
    };
  }
}
